import { useTranslation } from 'react-i18next';
import { DashboardCardWrapper } from '../../../components/DashboardCardWrapper/dashboard-card-wrapper';
import { ResponsiveBar, BarItemProps } from '@nivo/bar';
import { DashboardCardContentValue } from '../../../components/DashboardCardContentValue/dashboard-card-content-value';
import { toNumberString } from '@client/toolkit';
import { TiWarning } from 'react-icons/ti';
import { usePostContractsExpirationDashboardMutation } from '@client/api';
import { useEffect, useMemo } from 'react';
import { NoDataWidget } from '../NoDataWidget/no-data-widget';

const bucketColors = ['#F5BC6F', '#DE9634', '#CA7706', '#B56500', '#8B4513'];

const RoundedTopBar = (
  params: BarItemProps<{
    color: string;
    value: number;
    duration: string;
  }>,
) => {
  const { x, y, width, height, color } = params.bar;

  const radius = 8;
  const bottom = y + height;

  if (height === 0) return null;

  const path = `
    M${x},${bottom}
    L${x},${y + radius}
    Q${x},${y} ${x + radius},${y}
    L${x + width - radius},${y}
    Q${x + width},${y} ${x + width},${y + radius}
    L${x + width},${bottom}
    Z
  `;

  return <path d={path} fill={color} />;
};

interface ExpiringContractWidgetProps {
  buildingIds: string[];
  selectedDateFrom: string;
  selectedDateTo: string;
}

export const ExpiringContractWidget = ({ buildingIds, selectedDateFrom, selectedDateTo }: ExpiringContractWidgetProps) => {
  const { t } = useTranslation('widget');
  const [trigger, { data, isLoading }] = usePostContractsExpirationDashboardMutation();

  useEffect(() => {
    if (!selectedDateFrom || !selectedDateTo) return;
    trigger({
      startDate: selectedDateFrom,
      endDate: selectedDateTo,
      body: {
        buildingIds,
      },
    });
  }, [buildingIds, selectedDateFrom, selectedDateTo, trigger]);

  const buckets = useMemo(() => data?.buckets ?? [], [data?.buckets]);

  const totalExpiring = useMemo(() => buckets.reduce((sum, b) => sum + b.count, 0), [buckets]);

  const criticalBucket = useMemo(() => buckets.find((b) => b.isCritical), [buckets]);

  const chartData = useMemo(
    () =>
      buckets.map((bucket, index) => ({
        duration: bucket.label,
        value: bucket.count,
        color: bucketColors[index % bucketColors.length],
      })),
    [buckets],
  );

  if (data && totalExpiring === 0)
    return <NoDataWidget title={t('expiredContact.title')} noDataText={t('expiredContact.noData')} />;

  return (
    <DashboardCardWrapper
      to="/reporting/expire-contract"
      title={t('expiredContact.title')}
      tooltip={t('expiredContact.tooltip')}
      loading={!data || isLoading}
    >
      <div className="flex flex-row gap-12">
        <DashboardCardContentValue
          value={`${toNumberString(totalExpiring)}`}
          unit={t('expiredContact.totalExpiring')}
        />
        {criticalBucket && criticalBucket.count > 0 && (
          <div className="flex gap-2 items-center">
            <span className="text-4xl font-bold text-brand-primary">{criticalBucket.count}</span>
            <div className="flex flex-col" data-testid="expired-contract-warning">
              <TiWarning size={16} className="text-brand-primary" />
              <span className="flex items-end text-[0.86625rem] text-gray-300">
                {t('expiredContact.criticalPeriod')}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="w-full h-[200px]">
        <ResponsiveBar
          barComponent={RoundedTopBar}
          data={chartData}
          keys={['value']}
          indexBy="duration"
          margin={{ top: 20, right: 20, bottom: 50, left: 20 }}
          padding={0.3}
          enableLabel={false}
          enableGridY={false}
          colors={({ data }) => data.color}
          axisTop={null}
          axisRight={null}
          axisLeft={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 10,
            tickRotation: chartData.length > 3 ? -25 : 0,
            legend: '',
            legendPosition: 'middle',
            legendOffset: 32,
          }}
          theme={{
            background: '#191714',
            axis: {
              ticks: {
                text: {
                  fill: '#fff',
                  fontSize: 10,
                },
              },
            },
          }}
        />
      </div>
    </DashboardCardWrapper>
  );
};
